
import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Clock, AlertCircle, TrendingUp } from 'lucide-react';
import { AuditData } from '../../services/logistics/core/auditCalculations';

interface Props {
  data: AuditData;
  children: React.ReactNode;
}

export const BaseTooltip: React.FC<Props> = ({ data, children }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0, side: 'top' as 'top' | 'bottom' });
  const triggerRef = useRef<HTMLDivElement>(null);

  const updatePos = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Decisão inteligente de lado: se estiver muito no topo da tela, abre para baixo
      // Caso contrário, abre para cima (padrão)
      const side = rect.top < 180 ? 'bottom' : 'top';

      setPos({
        x: rect.left + rect.width / 2,
        y: side === 'top' ? rect.top : rect.bottom,
        side
      });
    }
  };

  const getIcon = () => {
    switch (data.type) {
      case 'financial': return <TrendingUp className="w-3 h-3 text-status-ready" />;
      case 'time': return <Clock className="w-3 h-3 text-sal-cyber-teal" />;
      case 'alert': return <AlertCircle className="w-3 h-3 text-status-warning" />;
      default: return <Calculator className="w-3 h-3 text-status-info" />;
    }
  };

  const getColor = () => {
    switch (data.type) {
      case 'financial': return 'text-status-ready border-status-ready/30';
      case 'time': return 'text-sal-cyber-teal border-sal-cyber-teal/30';
      case 'alert': return 'text-status-warning border-status-warning/30';
      default: return 'text-status-info border-status-info/30';
    }
  };

  return (
    <div
      ref={triggerRef}
      className="inline-flex cursor-help relative group"
      onMouseEnter={() => { updatePos(); setVisible(true); }}
      onMouseLeave={() => setVisible(false)}
      onClick={(e) => {
        e.stopPropagation();
        updatePos();
        setVisible(!visible);
      }}
    >
      {children}

      {createPortal(
        <AnimatePresence>
          {visible && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: pos.side === 'top' ? 10 : -10 }}
              animate={{ opacity: 1, scale: 1, y: pos.side === 'top' ? -12 : 12 }}
              exit={{ opacity: 0, scale: 0.95, y: pos.side === 'top' ? 10 : -10 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className={`fixed z-[10000] px-4 py-3 bg-[rgb(var(--tooltip-bg))]/90 backdrop-blur-xl border border-border-dim/40 text-[rgb(var(--tooltip-text))] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.4)] -translate-x-1/2 ${pos.side === 'top' ? '-translate-y-full' : 'translate-y-0'} whitespace-normal max-w-[280px] text-left leading-relaxed ring-1 ring-black/5 dark:ring-white/5 pointer-events-none`}
              style={{ left: pos.x, top: pos.y }}
            >
              <div className={`flex items-center gap-1.5 border-b border-border-dim pb-2 mb-2 ${getColor()}`}>
                {getIcon()}
                <span className="font-black uppercase text-xs tracking-[0.15em]">{data.title}</span>
              </div>

              <div className="text-xs text-[var(--tooltip-text)] opacity-90 mb-2 font-medium leading-snug">
                {data.description}
              </div>

              {data.formula && (
                <div className="bg-black/10 dark:bg-black/20 p-2 rounded-xl border border-border-dim/30 font-mono text-xs text-text-muted flex flex-col gap-1">
                  <span className="text-xs font-bold uppercase tracking-wider opacity-50">Base de Cálculo</span>
                  <span className={getColor().split(' ')[0]}>{data.formula}</span>
                </div>
              )}

              {/* Seta Dinâmica */}
              <div
                className={`absolute left-1/2 -translate-x-1/2 border-[6px] border-transparent ${pos.side === 'top'
                  ? 'top-full border-t-[rgb(var(--tooltip-bg))]'
                  : 'bottom-full border-b-[rgb(var(--tooltip-bg))]'
                  }`}
              />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};
